import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShieldLogo } from "../components/landing/Logo";
import { 
  Heart, Phone, Mail, CheckCircle2, Clock, AlertTriangle,
  Home, RefreshCw, Loader2, MessageSquare, TrendingUp,
  PhoneCall, UserCheck, XCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge colors
const STATUS_COLORS = {
  pending: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  contacted: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  resolved: "bg-green-500/20 text-green-400 border-green-500/30",
  escalated: "bg-red-500/20 text-red-400 border-red-500/30",
  needs_manual_callback: "bg-purple-500/20 text-purple-400 border-purple-500/30"
};

// Recovery Card Component
const RecoveryCard = ({ item, onUpdateStatus, onInitiateCallback }) => {
  const [showCallback, setShowCallback] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const handleCallback = async () => {
    if (!phoneNumber) {
      toast.error("Please enter a phone number");
      return;
    }
    setIsLoading(true);
    await onInitiateCallback(item.session_id, phoneNumber);
    setIsLoading(false);
    setShowCallback(false);
    setPhoneNumber("");
  };
  
  const handleStatusUpdate = async (status) => {
    await onUpdateStatus(item.session_id, status);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-xl border border-gold/20 p-5"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            item.agitation_score >= 0.8 ? "bg-red-500/20" : "bg-amber-500/20"
          }`}>
            <Heart className={`w-6 h-6 ${
              item.agitation_score >= 0.8 ? "text-red-400" : "text-amber-400"
            }`} />
          </div>
          <div>
            <h3 className="font-bold text-white">
              {item.guest_name || "Unknown Guest"}
            </h3>
            <p className="text-xs text-slate-400">
              {item.intent} • {new Date(item.timestamp).toLocaleString()}
            </p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-bold uppercase border ${STATUS_COLORS[item.recovery_status] || STATUS_COLORS.pending}`}>
          {item.recovery_status?.replace("_", " ") || "pending"}
        </span>
      </div>
      
      {/* Agitation Meter */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-400">Agitation Level</span>
          <span className={`text-sm font-bold ${
            item.agitation_score >= 0.8 ? "text-red-400" : 
            item.agitation_score >= 0.6 ? "text-amber-400" : "text-yellow-400"
          }`}>
            {Math.round(item.agitation_score * 100)}%
          </span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${
              item.agitation_score >= 0.8 ? "bg-red-500" : 
              item.agitation_score >= 0.6 ? "bg-amber-500" : "bg-yellow-500"
            }`}
            style={{ width: `${item.agitation_score * 100}%` }}
          />
        </div>
      </div>
      
      {/* Mood & Messages */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <div className="flex items-center gap-1 text-slate-400">
          <TrendingUp className="w-4 h-4" />
          <span>Mood: <span className="text-white font-medium">{item.mood}</span></span>
        </div>
        <div className="flex items-center gap-1 text-slate-400">
          <MessageSquare className="w-4 h-4" />
          <span><span className="text-white font-medium">{item.message_count || 0}</span> messages</span>
        </div>
      </div>
      
      {/* Action Buttons */}
      {item.recovery_status === "pending" && (
        <div className="space-y-3">
          {showCallback ? (
            <div className="flex gap-2">
              <Input
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="flex-1 bg-indigo-700/50 border-gold/20 text-white text-sm"
              />
              <Button
                onClick={handleCallback}
                disabled={isLoading}
                className="bg-green-600 hover:bg-green-500 text-white"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
              </Button>
              <Button
                onClick={() => setShowCallback(false)}
                variant="ghost"
                className="text-slate-400"
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                onClick={() => setShowCallback(true)}
                className="flex-1 bg-gold hover:bg-gold/80 text-indigo-700"
              >
                <Phone className="w-4 h-4 mr-2" /> Recovery Call
              </Button>
              <Button
                onClick={() => handleStatusUpdate("resolved")}
                variant="outline"
                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Resolved
              </Button>
              <Button
                onClick={() => handleStatusUpdate("escalated")}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <AlertTriangle className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}
      
      {item.recovery_status === "contacted" && (
        <div className="flex gap-2">
          <Button
            onClick={() => handleStatusUpdate("resolved")}
            className="flex-1 bg-green-600 hover:bg-green-500 text-white"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Resolved
          </Button>
          <Button
            onClick={() => handleStatusUpdate("escalated")}
            variant="outline"
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            Escalate
          </Button>
        </div>
      )}
      
      {item.recovery_status === "resolved" && (
        <div className="flex items-center gap-2 text-green-400 text-sm">
          <CheckCircle2 className="w-4 h-4" />
          <span>Issue resolved</span>
        </div>
      )}
    </motion.div>
  );
};

// Main Recovery Outreach Page
export default function RecoveryOutreach() {
  const { token } = useAuth();
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  
  const headers = { Authorization: `Bearer ${token}` };
  
  const fetchQueue = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/recovery/queue?status=${statusFilter}`, { headers });
      setQueue(res.data.queue || []);
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error("Failed to load recovery queue");
    } finally {
      setIsLoading(false);
    }
  }, [token, statusFilter]);
  
  useEffect(() => {
    if (token) fetchQueue();
  }, [token, fetchQueue]);
  
  const handleUpdateStatus = async (sessionId, newStatus) => {
    try {
      await axios.post(`${API}/recovery/update-status`, null, {
        headers,
        params: { session_id: sessionId, new_status: newStatus }
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchQueue();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };
  
  const handleInitiateCallback = async (sessionId, phoneNumber) => {
    try {
      const res = await axios.post(`${API}/recovery/initiate-callback`, null, {
        headers,
        params: { session_id: sessionId, phone_number: phoneNumber }
      });
      toast.success(res.data.message);
      fetchQueue();
    } catch (err) {
      toast.error("Failed to initiate callback");
    }
  };
  
  return (
    <div className="min-h-screen bg-[#040F49]" data-testid="recovery-outreach">
      <div className="noise-overlay" />
      
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-indigo-700/90 border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldLogo size="sm" />
            <span className="font-inter text-sm font-bold text-gold tracking-wider">RECOVERY OUTREACH</span>
            <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold uppercase">
              {stats.pending || 0} Pending
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchQueue} className="text-slate-400 hover:text-white">
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            </button>
            <a href="/admin/staff-sync" className="text-slate-400 hover:text-gold text-xs">Staff Sync</a>
            <a href="/admin/founding-50" className="text-slate-400 hover:text-gold text-xs">Dashboard</a>
            <a href="/" className="text-slate-400 hover:text-white">
              <Home className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card rounded-lg border border-amber-500/30 p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.pending || 0}</p>
                <p className="text-xs text-slate-400">Pending Recovery</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-lg border border-green-500/30 p-4">
            <div className="flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.resolved || 0}</p>
                <p className="text-xs text-slate-400">Guests Recovered</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-lg border border-red-500/30 p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-2xl font-bold text-white">{Math.round((stats.average_agitation || 0) * 100)}%</p>
                <p className="text-xs text-slate-400">Avg Agitation</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-lg border border-gold/30 p-4">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8 text-gold" />
              <div>
                <p className="text-2xl font-bold text-white">
                  {stats.resolved && stats.pending ? Math.round((stats.resolved / (stats.resolved + stats.pending)) * 100) : 0}%
                </p>
                <p className="text-xs text-slate-400">Recovery Rate</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {["pending", "contacted", "resolved", "all"].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status
                  ? "bg-gold text-indigo-700"
                  : "bg-indigo-700/50 text-slate-300 hover:bg-indigo-700/70"
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
        
        {/* Queue */}
        <div className="grid md:grid-cols-2 gap-4">
          {isLoading ? (
            <div className="col-span-2 flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gold" />
            </div>
          ) : queue.length === 0 ? (
            <div className="col-span-2 text-center py-12">
              <Heart className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <p className="text-slate-400">No {statusFilter} recovery items</p>
              <p className="text-sm text-slate-500 mt-1">Your guests are happy!</p>
            </div>
          ) : (
            queue.map((item, idx) => (
              <RecoveryCard
                key={item.session_id || idx}
                item={item}
                onUpdateStatus={handleUpdateStatus}
                onInitiateCallback={handleInitiateCallback}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
